// Constants
import { EDUCATION_PATTERN } from '@/constants/regex';
import { LIST_TRUNCATE_LIMIT } from '@/constants/chatbot';

// Utils
import {
  normalizeString,
  formatTruncatedList,
  formatListWithAnd,
} from '@/utils/textUtils';

// Types
import type { EmployeeEducation } from '@/types/employee';

export interface GroupedEducation {
  school: string;
  entries: EmployeeEducation[];
}

/** Group educations by school name */
export const groupEducationsBySchool = (
  educations: EmployeeEducation[],
): GroupedEducation[] => {
  const grouped = new Map<string, EmployeeEducation[]>();

  for (const edu of educations) {
    const school = edu.school || '';
    // Skip educations without school name
    if (!school) continue;

    const existing = grouped.get(school);
    if (existing) {
      existing.push(edu);
    } else {
      grouped.set(school, [edu]);
    }
  }

  return [...grouped.entries()].map(([school, entries]) => ({
    school,
    entries,
  }));
};

// Creates unique key from school, degree, and major for matching
const createEducationKey = (e: EmployeeEducation): string =>
  [e.school, e.degree, e.major]
    .map((v) => normalizeString(v || ''))
    .filter(Boolean)
    .join('|');

export const matchEducationByKeyFields = (
  a: EmployeeEducation,
  b: EmployeeEducation,
): boolean => createEducationKey(a) === createEducationKey(b);

export const isEducationInError = (
  e: EmployeeEducation,
  errorText: string,
): boolean => {
  const normalized = normalizeString(errorText);
  const school = normalizeString(e.school || '');
  if (!school || !normalized.includes(school)) return false;

  const degree = normalizeString(e.degree || '');
  const major = normalizeString(e.major || '');

  // If degree is mentioned, verify major is also present or not required
  if (degree && normalized.includes(degree)) {
    return !major || !normalized.includes(' in ') || normalized.includes(major);
  }

  // If error mentions degree keywords but education has degree, it's a mismatch
  if (EDUCATION_PATTERN.DEGREE_KEYWORDS.test(normalized) && degree) {
    return false;
  }

  return true;
};

export const extractSchoolNames = (rawMsg: string): string[] => {
  // Try extracting from "school" field pattern first
  const matches = [...rawMsg.matchAll(EDUCATION_PATTERN.SCHOOL_FIELD)];
  const fromField = matches
    .map((m) => m[1])
    .filter((s): s is string => Boolean(s));

  if (fromField.length) return fromField;

  // Fallback to extracting school name after "at" keyword
  const atMatch = rawMsg.match(EDUCATION_PATTERN.SCHOOL_AFTER_AT);
  return atMatch?.[1] ? [atMatch[1].trim()] : [];
};

export const buildEducationSuccessMessage = (
  addSucceeded: EmployeeEducation[],
  removeSucceeded: EmployeeEducation[],
): string => {
  const formatAction = (
    educations: EmployeeEducation[],
    action: 'added' | 'removed',
  ) => {
    // Get unique school names to avoid duplicates in message
    const schools = [
      ...new Set(educations.map((e) => e.school).filter(Boolean)),
    ];

    // If no valid school names, return empty string to use fallback message
    if (schools.length === 0) return '';

    const list = formatTruncatedList(
      schools.map((s) => `Degree in ${s}`),
      LIST_TRUNCATE_LIMIT,
      formatListWithAnd,
    );
    return `${list} ${schools.length === 1 ? 'has' : 'have'} been ${action}`;
  };

  const hasAdded = addSucceeded.length > 0;
  const hasRemoved = removeSucceeded.length > 0;

  if (hasAdded && hasRemoved) {
    const addedMsg = formatAction(addSucceeded, 'added');
    const removedMsg = formatAction(removeSucceeded, 'removed');

    // If both messages are empty, return empty string
    if (!addedMsg && !removedMsg) return '';
    // If one is empty, return the other
    if (!addedMsg) return removedMsg;
    if (!removedMsg) return addedMsg;

    return `${addedMsg} and ${removedMsg}`;
  }

  if (hasAdded) return formatAction(addSucceeded, 'added');
  if (hasRemoved) return formatAction(removeSucceeded, 'removed');

  return '';
};
